// src/components/NoteCard.js
import React, { useState, useRef, useEffect } from "react";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { ChevronDown, ChevronUp, ThreeDotsVertical, PencilSquare, Trash } from "react-bootstrap-icons";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from 'react-i18next';

const NoteCard = ({ note, colorCategories, onEdit, onDelete, highlightQuery }) => {
  const { t } = useTranslation();
  const colorObj = colorCategories.find(cat => cat.id === note.categoryId);
  const color = colorObj ? colorObj.color : "#999";

  const MAX_LINES = 1;  

  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const textRef = useRef(null);

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    onDelete(note._id);
    setShowConfirm(false);
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.split(regex).map((part, idx) =>
      regex.test(part) ? <mark key={idx} style={{ backgroundColor: "#c48f82ff" }}>{part}</mark> : part
    );
  };

  const formattedDate = note.createdAt
    ? new Date(note.createdAt).toLocaleString('fr-FR', {
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '';

  // Vérifie si le texte dépasse MAX_LINES lignes
  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseFloat(getComputedStyle(textRef.current).lineHeight);
      const maxHeight = lineHeight * MAX_LINES;
      if (textRef.current.scrollHeight > maxHeight) setCanExpand(true);
    }
  }, [note.text]);

  return (
    <div
      className="note-card"
      style={{
        borderRadius: "6px",
        padding: "0px",
        marginBottom: "15px",
        boxShadow: `0px -3px 1px ${color}`,
        color: "#f5f5f5",
        position: "relative",
        // overflow: "hidden",
      }}
    >
      {/* --- Header --- */}
      <div
        className="note-card-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 12px",
          backgroundColor: "#2B2F38",
          lineHeight: "1.2",
        }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (canExpand) setExpanded(prev => !prev);
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            cursor: canExpand ? "pointer" : "default",
          }}
        >
          {/* Première ligne : titre à gauche */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span
              style={{
                fontWeight: "600",
                fontSize: "1.1em",
                color: "#ddd",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {highlightText(note.title || t(`note.input.title`), highlightQuery)}
            </span>
          </div>

          {/* Deuxième ligne : date à droite */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <span
              style={{
                fontSize: "0.8em",
                color: "#aaa",
                marginTop: "-2px",
                marginBottom: "2px",
                marginRight: "10px"
              }}
            >
              {formattedDate}
            </span>
          </div>
        </div>

        {/* --- Menu trois points --- */}
       {onEdit && (
        <Dropdown>
    <Dropdown.Toggle
      as="button"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        color: "#ccc",
        cursor: "pointer",
      }}
    >
      <ThreeDotsVertical size={18} />
    </Dropdown.Toggle>

  <Dropdown.Menu
    style={{
      backgroundColor: "#2B2F38",
      border: `1px solid #A3A3A3`,
      borderTop:"0px",
      borderRadius: "0px 0px 8px 8px",
      padding: "0",
      paddingTop:"0rem",
      minWidth: "unset",
      width: "42px",
      marginTop:"8px"
    }}
  >
    <Dropdown.Item
      onClick={() => onEdit && onEdit(note)}
      style={{
        padding: "8px",
        paddingTop:"0px",
        paddingBottom:"4px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "none",
      }}
    >
      <PencilSquare size={12} color="#A3A3A3" />
    </Dropdown.Item>

    <Dropdown.Item
      // onClick={() => onDelete && onDelete(note._id)}
      onClick={handleDelete}
      style={{
        padding: "4px",
        paddingBottom:"8px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "none",
      }}
    >
      <Trash size={24} color="#ff6b6b" />
    </Dropdown.Item>
  </Dropdown.Menu>
</Dropdown>
)}
      </div>

      {/* --- Contenu --- */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (canExpand) setExpanded(prev => !prev);
        }}
        style={{
          padding: "10px 16px",
          background: "linear-gradient(145deg, #4A4F5C, #4A4F5C)",
          cursor: canExpand ? "pointer" : "default",
        }}
      >
        <p
          ref={textRef}
          style={{
            margin: 0,
            fontSize: "1em",
            lineHeight: "1.4",
            textAlign: "left",
            whiteSpace: "pre-line",
            display: "-webkit-box",
            WebkitLineClamp: expanded ? "none" : MAX_LINES,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            transition: "all 0.3s ease",
            color: "#ddd",
          }}
        >
          {highlightText(note.text, highlightQuery)}
        </p>

        {canExpand && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "8px",
              color: "#f5f5f5",
            }}
          >
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        )}
      </div>
      {/* --- Modal de confirmation --- */}
      <ConfirmDeleteModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default NoteCard;
